import { contractsUtils } from 'api/services/contracts';
import { Avatar, Button } from 'components';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { formatDateWithMonthYear } from 'utils';
import { ROUTES } from 'utils/constant';
import { CONTRACT_KEYS } from 'pages/Contracts/constants';
import { CONTRACT_TABS } from 'pages/Contracts/constants';
import './ContractCard.scss';

const {
  IS_OWNED_BY_USER,
  TITLE,
  BUSINESS_NAME,
  AMOUNT_USD,
  PAYMENT_TYPE,
  EXPECTED_DURATION_DAYS,
  EXPERTISE_LEVEL,
  TECH_STACK,
  PUBLIC_ID,
  CREATED_AT,
} = CONTRACT_KEYS;

const { PROPOSALS } = CONTRACT_TABS;

const MAX_VISIBLE_SKILLS = 5;

const ContractCard = ({ contract, onViewDetails }) => {
  const navigate = useNavigate();

  const handleSubmitProposal = (e) => {
    e.stopPropagation();

    // Open proposal form if user is not owner
    if (!contract[IS_OWNED_BY_USER]) {
      navigate(
        ROUTES.CONTRACT_SUBMIT_PROPOSAL.replace(
          ':contractId',
          contract[PUBLIC_ID]
        )
      );
    }
  };

  const proposalHandler = (e) => {
    e.stopPropagation();

    if (!contract[PUBLIC_ID]) {
      return;
    }

    if (contract[IS_OWNED_BY_USER]) {
      navigate(
        ROUTES.CONTRACT_DETAILS.replace(':contractId', contract[PUBLIC_ID]),
        {
          state: { initialTab: PROPOSALS.value },
        }
      );
      return;
    }

    handleSubmitProposal(e);
  };

  // Default stats for demo if missing
  const views = contract?.views ?? 0;
  const proposals = contract?.statistics?.submission_count ?? 0;
  const milestones = contract?.statistics?.milestone_count ?? 0;

  return (
    <div className='contract-card-dashboard'>
      <div className='details-column'>
        <div className='contract-header'>
          <div className='contract-title'>{contract[TITLE]}</div>
        </div>

        <div className='contract-company'>
          <Avatar
            name={contract[BUSINESS_NAME]}
            size='sm'
            className='company-avatar'
          />
          <div className='company-info'>
            <div className='company-name'>{contract[BUSINESS_NAME]}</div>
          </div>
        </div>

        <div className='meta-row'>
          <span className='money-style '>
            $
            {contractsUtils
              .formatCurrency(contract[AMOUNT_USD])
              .replace('$', '')}
          </span>
          <span className='meta-item'>{contract[PAYMENT_TYPE]}</span>
          {contract[EXPECTED_DURATION_DAYS] && (
            <span className='meta-item'>
              {contract[EXPECTED_DURATION_DAYS]} days
            </span>
          )}
          <span className='meta-item'>{contract[EXPERTISE_LEVEL]}</span>
          <span className='meta-item'>Milestones: {milestones}</span>
          {proposals ? (
            <span className='meta-item'>
              Proposals: <strong>{proposals}</strong>
            </span>
          ) : null}
        </div>

        <div className='footer-row'>
          <div className='stats-wrapper'>
            {views ? (
              <div className='stat-item'>
                <strong>{views}</strong> views
              </div>
            ) : null}
          </div>
        </div>

        {/* Skills/Tech Stack Section */}
        {contract?.[TECH_STACK]?.length > 0 && (
          <div className='skills-row'>
            <div className='skills-label'>Skills:</div>
            <div className='skills-list'>
              {contract[TECH_STACK].slice(0, MAX_VISIBLE_SKILLS).map(
                (skill) => (
                  <span key={skill} className='skill-badge'>
                    {skill}
                  </span>
                )
              )}
              {contract[TECH_STACK].length > MAX_VISIBLE_SKILLS && (
                <span className='skill-more'>
                  +{contract[TECH_STACK].length - MAX_VISIBLE_SKILLS} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='actions-column'>
        <div className='contract-date'>
          {formatDateWithMonthYear(contract[CREATED_AT])}
        </div>

        <Button
          variant='secondary'
          className='btn-details'
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(contract[PUBLIC_ID]);
          }}
        >
          Details
        </Button>
        <Button onClick={proposalHandler} variant='primary'>
          {contract[IS_OWNED_BY_USER] ? (
            <span>Proposals</span>
          ) : (
            <span>Proposal</span>
          )}
        </Button>
      </div>
    </div>
  );
};

ContractCard.propTypes = {
  contract: PropTypes.shape({
    public_id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    business_name: PropTypes.string.isRequired,
    amount_usd: PropTypes.number,
    payment_type: PropTypes.string,
    expected_duration_days: PropTypes.number,
    expertise_level: PropTypes.string,
    tech_stack: PropTypes.arrayOf(PropTypes.string),
    views: PropTypes.number,
    statistics: PropTypes.shape({
      submission_count: PropTypes.number,
      milestone_count: PropTypes.number,
    }),
    created_at: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]).isRequired,
    is_owned_by_user: PropTypes.bool,
  }).isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

export default ContractCard;
